import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { formatAUD } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PayPalSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [state, setState] = useState({ loading: true, paid: false, amount: null });

  useEffect(() => {
    if (!orderId) {
      setState({ loading: false, paid: false });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/paypal/status/${orderId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, paid: true, amount: data.amount / 100 });
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else setState({ loading: false, paid: false });
    };
    poll();
  }, [orderId]);

  return (
    <div data-testid="paypal-success-page" className="min-h-screen bg-marble flex items-center justify-center px-6">
      <div className="marble-veil" />
      <div className="max-w-md w-full bg-white/80 backdrop-blur-xl border border-gold/30 rounded-sm p-12 text-center">
        {state.loading ? (
          <>
            <Loader2 size={40} className="text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-serif-lux text-3xl text-charcoal">Confirming your order…</h1>
          </>
        ) : (
          <>
            <CheckCircle2 size={48} strokeWidth={1.25} className="text-gold mx-auto mb-6" />
            <h1 data-testid="paypal-order-confirmed-heading" className="font-serif-lux text-4xl text-charcoal mb-4">
              {state.paid ? "Order confirmed" : "We're checking your payment"}
            </h1>
            <p className="text-sm text-charcoal/60 font-light leading-relaxed mb-2">
              {state.paid
                ? "Thank you for choosing Altius. Your PayPal payment was successful."
                : "Your PayPal payment is taking a little longer to confirm. If you were charged, your order is safe with us."}
            </p>
            {state.paid && state.amount != null && (
              <p data-testid="paypal-order-amount" className="font-serif-lux text-2xl text-gold-dark mb-8">
                {formatAUD(state.amount)}
              </p>
            )}
            <Link
              data-testid="paypal-back-to-shop-link"
              to="/"
              className="inline-block px-10 py-4 bg-gold text-charcoal text-xs tracking-[0.25em] uppercase font-semibold rounded-full hover:bg-gold-dark transition-colors mt-6"
            >
              Continue Shopping
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
